import * as React from 'react';
import { LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { Typography } from 'omakase';
import { centerCropThumbnailUrl } from '@tock/images';
import { consumer } from '@tock/coretypes';
import { getAltTextWithFallback, getConcept } from 'utils/businessUtils';
import { oc } from 'ts-optchain';
import BaseComponent from '@tock/shared/components/BaseComponent';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import PhotoItemDisplay from 'components/PhotoItemDisplay';
import _ from 'lodash';
import connect, { DefaultConnectProps, StoreType } from '@tock/shared/utils/connect';

interface ConnectProps extends DefaultConnectProps {
  availability: StoreType['availability'];
}

interface OwnProps {
  date: string;
  time: string;
  partySize: number;
  message: React.ReactNode;
}

type Props = OwnProps & ConnectProps;

class ReservationSearchPartners extends BaseComponent<Props> {
  componentDidMount() {
    this.handleAvailabilitySearch();
  }

  componentDidUpdate(prevProps: Props) {
    const { date, time, partySize } = this.props;
    // If any of the reservation params have changed perform a new availability search
    if (prevProps.date !== date || prevProps.time !== time || prevProps.partySize !== partySize) {
      this.handleAvailabilitySearch();
    }
  }

  handleAvailabilitySearch() {
    const {
      app: { config },
      date,
      partySize,
      time,
    } = this.props;

    const businessGroupId = config?.business?.businessGroup?.id;
    const city = config?.business?.nearestMetro || config?.business?.city;
    this.props.actions.availability.search(
      {
        dateTime: {
          min: LocalDateTime.of(LocalDate.parse(date), LocalTime.parse(time)).minusHours(2.5),
          max: LocalDateTime.of(LocalDate.parse(date), LocalTime.parse(time)).plusHours(2.5),
        },
        size: {
          min: partySize,
          max: partySize,
        },
        type: 'ALL_EXPERIENCES',
      },
      {
        additionalQuery: {
          businessGroupId: businessGroupId ? [businessGroupId] : undefined,
          city: city ? [city] : undefined,
        },
      }
    );
  }

  render() {
    const {
      app: { config },
      availability,
      message,
      date,
      partySize,
      time,
    } = this.props;
    const isLoading = availability.requestInProgress || !availability.isInitialized;
    const partnerResults = availability.result
      ? (availability.result.offeringAvailability as consumer.SearchOfferingBusiness[])
      : [];
    return (
      <LoadingComponent isLoading={isLoading}>
        {partnerResults.length === 0 ? null : (
          <div className="ReservationSearchPartners">
            <p>{message}</p>
            <hr className="ReservationSearchPartners-separator" />
            <ul className="ReservationSearchPartners-restaurauntList">
              {_.map(partnerResults, (result) => {
                const business = result.business as consumer.OfferingBusiness;
                if (!business) {
                  return null;
                }
                const profileImage = oc(business).profileImage[0]();
                const imgSrc = centerCropThumbnailUrl(oc(profileImage).imageUrl(''));
                return (
                  <li key={business.id} className="ReservationSearchPartners-restaurauntListItem">
                    <PhotoItemDisplay
                      title={
                        <Typography component="span" variant="body02Strong">
                          {business.name}
                        </Typography>
                      }
                      subtitle={
                        <p>
                          <Typography component="span" variant="caption">
                            {getConcept(business)}
                          </Typography>
                        </p>
                      }
                      subtitleString={getConcept(business)}
                      imgSrc={imgSrc}
                      dominantColor={oc(profileImage).dominantColor('#e9e9eb')}
                      altText={getAltTextWithFallback(oc(profileImage).altText(null), business)}
                      showBodyByDefault={false}
                      href={`/${
                        business.domainName
                      }/search?date=${date}&time=${time}&size=${partySize}&tock_source=tock&tock_medium=sold_out_search&tock_content=sold_out_${oc(
                        config
                      ).business.city()}`}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </LoadingComponent>
    );
  }
}

export default connect<OwnProps, ConnectProps>(ReservationSearchPartners, ['availability']);
